import { format } from "d3-format";
import React, { Component } from "react";
import PropTypes from "prop-types";

import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { functor } from "../utils";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class MACDTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const { onClick, fontFamily, fontSize, displayFormat, className, bgFill } =
      this.props;
    const { yAccessor, options, appearance, labelFill } = this.props;
    const { displayValuesFor } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);
    const macdValue = currentItem && yAccessor(currentItem);

    const macd =
      (macdValue && macdValue.macd && displayFormat(macdValue.macd)) || "n/a";
    const signal =
      (macdValue && macdValue.signal && displayFormat(macdValue.signal)) ||
      "n/a";
    const divergence =
      (macdValue &&
        macdValue.divergence &&
        displayFormat(macdValue.divergence)) ||
      "n/a";

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    // Safety fallbacks for appearance
    const macdColor = appearance?.stroke?.macd || labelFill || "#2962FF";
    const signalColor = appearance?.stroke?.signal || labelFill || "#FF6D00";
    const divergenceColor =
      appearance?.fill?.divergence || labelFill || "#26a69a";

    // Calculate text width for background
    const fullText = `MACD (${options.slow}, ${options.fast}): ${macd} Signal (${options.signal}): ${signal} Divergence: ${divergence}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgWidth = textWidth + TOOLTIP_BG_PADDING * 2;

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-macd"
      >
        {/* Background rectangle */}
        <rect
          x={-TOOLTIP_BG_PADDING}
          y={-5}
          width={bgWidth}
          height={TOOLTIP_BG_HEIGHT}
          fill={bgFill || "#1f2937"}
          rx={6}
          ry={6}
        />
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          <ToolTipTSpanLabel fill={labelFill}>MACD (</ToolTipTSpanLabel>
          <tspan fill={macdColor}>{options.slow}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>, </ToolTipTSpanLabel>
          <tspan fill={macdColor}>{options.fast}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>): </ToolTipTSpanLabel>
          <tspan fill={macdColor}>{macd}</tspan>
          <ToolTipTSpanLabel fill={labelFill}> Signal (</ToolTipTSpanLabel>
          <tspan fill={signalColor}>{options.signal}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>): </ToolTipTSpanLabel>
          <tspan fill={signalColor}>{signal}</tspan>
          <ToolTipTSpanLabel fill={labelFill}> Divergence: </ToolTipTSpanLabel>
          <tspan fill={divergenceColor}>{divergence}</tspan>
        </ToolTipText>
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

MACDTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,

  yAccessor: PropTypes.func.isRequired,
  options: PropTypes.shape({
    slow: PropTypes.number.isRequired,
    fast: PropTypes.number.isRequired,
    signal: PropTypes.number.isRequired,
  }).isRequired,
  appearance: PropTypes.shape({
    stroke: PropTypes.shape({
      macd: PropTypes.string.isRequired,
      signal: PropTypes.string.isRequired,
    }).isRequired,
    fill: PropTypes.shape({
      divergence: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  displayFormat: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
};

MACDTooltip.defaultProps = {
  origin: [0, 0],
  displayFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  className: "react-stockcharts-tooltip",
};

export default MACDTooltip;
// export default MACDTooltip;
